import React from "react";
import styled from "styled-components";
import { Link } from 'react-router-dom';
import Button from '../../Button';
import { centsToCurrencyString } from '../../../utils/numbers';
import { breakpoints } from "../../../utils/styles";
import { PictureWithSources } from "../../common/PictureWithSources";

const TitleIconWrapper = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 0.75rem;

    @media screen and (min-width: ${breakpoints.desktop}px) {
        flex-direction: column-reverse;
        justify-content: center;
    }
  `;

const IconContainer = styled.div`
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    @media screen and (min-width: ${breakpoints.desktop}px) {
        margin: 1rem 0 0.75rem 0;
    }
`;

const LevelImage = styled(PictureWithSources)`
    max-width: 40px;
    object-fit: contain;
`;

const LevelIcon = styled.img`
    opacity: 0.5;
    position: absolute;
    right: -2rem;
    top: -2rem;
    width: 195px;

    @media screen and (min-width: ${breakpoints.desktop}px) {
        left: 50%;
        opacity: 1;
        top: 1em;
        transform: translateX(-50%);
        width: auto;
    }
`;

const LevelEmoji = styled.p`
    font-size: 2rem;
    margin: 0 !important;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const LevelTitle = styled.h2`
    color: var(--text-primary) !important;
    font-size: 2rem !important;
    font-weight: 700;
    margin: 0.5em 0 1em;

    body.theme-market & {
        text-transform: uppercase !important;
    }
        
    body.theme-platinum & {
        color: var(--text-primary) !important;
        font-size: ${(16 / 12.6).toFixed(2)}rem !important;
        font-weight: 400 !important;
        line-height: 1.5;
    }
        
    @media screen and (min-width: ${breakpoints.desktop}px) {
        font-size: 1.65rem !important;
    }
`;

const LevelBenefits = styled.ul`
    margin-bottom: 1em !important;
    padding: 0 0 0 1.5em;
    text-align: left;

    & > li { margin-bottom: 0.5em; }
    & > li > strong {
        color: var(--accent);
        body.theme-platinum & { color: var(--primary); }
    }
`;

const LevelPrice = styled.p`
    font-size: 1.75em;
    font-weight: 700;
    margin: 0.75em 0 0.5em 0 !important;
`;

const LevelBox = styled.div<{selected: boolean, altStyles?: boolean}>`
    background: var(--page-bg);
    border: 1px solid var(--text-primary);
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-bottom: 2em;
    overflow: hidden;
    position: relative;

    @media screen and (min-width: ${breakpoints.desktop}px) {
        margin-bottom: 0;
        text-align: center;
    }

    & ${LevelTitle} { ${props => props.altStyles ? 'font-size: 1.2em;' : ''} }

    ${props => props.selected && `
        &:before {
        content: '';
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        border: 2px solid var(--text-primary);
        }
    `}

    ${props => props.selected && `
        body.theme-platinum & {
        &, &:before { border-color: var(--primary); }
        }
    `}

    body.theme-platinum & ${LevelTitle} {
        ${props => props.selected && `
        color: var(--primary) !important;
        `}
    }
`;

const LevelContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    line-height: 1.5;
    padding: 1rem 2rem;
    transition: border-color 0.25s ease-out;
    width: 100%;

    & p {
        margin: 1em 0 0.5em 0;
    }
    
    @media screen and (min-width: ${breakpoints.desktop}px) {
        border-bottom-color: transparent !important;
        font-size: 0.7em;

        body.theme-market & { font-size: 1em; }
    }
`;

const LevelButton = styled(Button)`
    bottom: 1.5em;
    position: absolute;
    right: 1.5em;
    
    @media screen and (min-width: ${breakpoints.desktop}px) {
        bottom: auto;
        margin-bottom: 1.5em;
        position: relative;
        right: auto;
    }

    ${LevelBox}:hover & {
        background: var(--primary);
        --local-border-color: var(--primary);
        color: var(--text-tertiary);
    }
`;

const TermsLinks = styled.p`
    font-size: 0.8em;
    margin-top: auto !important; // Empuja los términos y el precio hacia abajo

    @media screen and (min-width: ${breakpoints.desktop}px) {
        font-size: 1em;
    }
`;

function getOptionTitle(option) {
  let title = '';
  
  if (('is_current' in option) && option.is_current === false) {
    title = 'Subir a ';
  }

  title += option.name;

  return title;
}

function formatText(text) {
  return text.replace(/\*(.+)\*/, '<strong>$1</strong>');
}

type LoyaltyOptionType = {
    benefits?: string[];
    description?: string;
    icon?: string;
    id: string;
    image?: string;
    images?: {
        icon?: { [key: string]: string };
        image?: { [key: string]: string };
    };
    is_current?: boolean;
    name: string;
    price: number;
    terms_url?: string;
};

interface Props {
    altStyles?: boolean;
    option: LoyaltyOptionType;
    selected: boolean;
    onSelect: (option: LoyaltyOptionType) => void;
}

function LoyaltyPurchaseOption(props: Props) {
    const { altStyles, option, selected, onSelect } = props;

    return <LevelBox key={option.id} onClick={e => { e.stopPropagation(); onSelect(option); }} selected={selected} className="fade-in" altStyles={altStyles}>
        <LevelContent>
            <TitleIconWrapper>
                <LevelTitle className='title-2'>{getOptionTitle(option)}</LevelTitle>
                <Icon option={option} />
            </TitleIconWrapper>

            {option.benefits && <Benefits benefits={option.benefits} />}
            {option.description && <p>{option.description.split("\n").map((line, index, array) => (
                <React.Fragment key={index}>
                    {line}
                    {index < array.length - 1 && <br />}
                </React.Fragment>
            ))}</p>}

            <TermsLinks>
                {option.terms_url ? <React.Fragment><a href={option.terms_url}>Consulta términos y condiciones.</a><br /></React.Fragment> : ''}
                {option.benefits && <Link to="/loop/niveles-beneficios">Ver todos los beneficios.</Link>}
            </TermsLinks>

            <LevelPrice>{centsToCurrencyString(option.price)}</LevelPrice>
        </LevelContent>
        <LevelButton primary={selected}>Seleccionar</LevelButton>
    </LevelBox>
}

function Icon({ option }: { option: LoyaltyOptionType }) {
    if ((!option.images || !option.images.icon) && !option.image && !option.icon) {
        return null;
    }

    return <IconContainer>
        {option.images ? <LevelImage images={option.images.icon} fallback={option.image} title={option.name} /> : ''}
        {!option.images && option.icon && !option.image ? <LevelEmoji>{option.icon}</LevelEmoji> : ''}
    </IconContainer>;
}

function Benefits({benefits}: {benefits: string[]}) {
    const count = benefits.length;
    if (1 === count) {
        return <p>{benefits[0]}</p>;
    }

    if (count > 1) {
        return <LevelBenefits>
            {benefits.map((benefit, index) => <li key={index} dangerouslySetInnerHTML={{ __html: formatText(benefit) }} />)}
        </LevelBenefits>;
    }
    
    return null;
}

export default LoyaltyPurchaseOption;